import { useCallback } from 'react';
import { shallowEqual } from 'react-redux';

import { Accordion, Box, Checkbox, FormField, Label, Paragraph, Text, Textarea } from '@zillow/constellation-10';

import stringHelper from '../../../../utils/stringHelper';
import { useSelector } from '../../../../selectors/useSelector';

import DeactivationModalCSEvents, {
  TRIGGER_SOURCE_MAP,
} from '../../../../model/ClickstreamEvent/DeactivationModalCSEvents';

type Props = {
  hideHeading?: boolean;
  leads: Array<{ name: string; isIncluded: boolean }>;
  message: string;
  setMessage: (message: string) => void;
  toggleIncludeLead: (index: number) => void;
};

const NotifyLeads = ({
  hideHeading = false,
  leads = [],
  message = '',
  setMessage = () => {},
  toggleIncludeLead = () => {},
}: Props) => {
  const { currentProperty, user } = useSelector(
    (state) => ({ currentProperty: state.properties.currentProperty, user: state.user }),
    shallowEqual,
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(e.target.value);
    },
    [setMessage],
  );
  const numIncludedLeads = leads.filter(({ isIncluded }) => isIncluded).length;
  return (
    <Box css={{ marginTop: hideHeading ? '0' : '400', marginBottom: hideHeading ? '0' : '600' }}>
      {!hideHeading && <Text textStyle="heading-sm">Notify renters who you haven&apos;t responded to yet?</Text>}

      <Paragraph css={{ marginBlock: hideHeading ? '0' : '400', marginBottom: '400' }}>
        Our records show you haven&apos;t replied to {leads.length} other{' '}
        {stringHelper.pluralizeWord(leads.length, 'renter', 'renters')} who contacted you about this property. Consider
        sending a message to let them know it&apos;s no longer available so they can focus on other listings.
      </Paragraph>

      <FormField
        control={<Textarea onChange={onChange} value={message} rows={8} />}
        label={<Label>Customize your message below:</Label>}
      />

      <Accordion.Root css={{ marginBlock: '400' }}>
        <Accordion.Item value="notify-leads-recipients">
          <Accordion.Header>
            <Text textStyle="body-bold" css={{ color: 'text.subtle' }}>
              Recipients
            </Text>
            <Text>
              {numIncludedLeads} {stringHelper.pluralizeWord(numIncludedLeads, 'person', 'people')}
            </Text>
          </Accordion.Header>
          <Accordion.Panel>
            <Paragraph color="text.subtle" css={{ marginBlock: '400' }}>
              If you already responded to one of these renters, you can remove them from receiving this message.
            </Paragraph>
            {leads.map(({ name, isIncluded }, i) => (
              <Box
                css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBlock: '600' }}
                key={`lead-${i}`}
              >
                <Paragraph className="fs-mask">{name}</Paragraph>
                <Checkbox
                  onChange={() => {
                    toggleIncludeLead(i);

                    DeactivationModalCSEvents.DynamicNotifyLeads({
                      user,
                      currentProperty,
                      triggerSource: isIncluded ? TRIGGER_SOURCE_MAP.DELETE : TRIGGER_SOURCE_MAP.ADD,
                    });
                  }}
                  checked={isIncluded}
                  data-eventid={DeactivationModalCSEvents.DynamicNotifyLeads.data_event_id}
                />
              </Box>
            ))}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion.Root>
    </Box>
  );
};

export default NotifyLeads;
